<template>
  <div class="solel-boneh-wrapper">
    <!-- Select Position State -->
    <div v-if="!selectedPositionId" class="box my-4 has-text-centered">
      <h4 class="title is-4">🎯 משימה יעודית</h4>
      <p class="subtitle is-5">אנא בחר משרה להתחלת המשימה</p>
      <div class="mt-4">
        <button @click="openPositionSwitchModal" class="button is-primary is-large">
          <span class="icon"><i class="fas fa-search"></i></span>
          <span>בחר משרה</span>
        </button>
      </div>
      <div class="mt-4">
        <router-link :to="{ name: 'admin-positions', query: { statuses: 'active', is_hot: 'true' } }" class="button is-info is-medium">
          <span class="icon"><i class="fas fa-list"></i></span>
          <span>עמוד המשרות</span>
        </router-link>
      </div>
    </div>

    <!-- Confirmation UI -->
    <div v-else-if="showConfirmationUI" class="box my-4 has-background-light">
      <div class="content has-text-right">
        <h4 class="title is-4">🎯 משימה יעודית</h4>
        <h5 class="subtitle is-5 mt-2">
          משרה נבחרה:
          <span v-if="selectedPosition && selectedPosition.id" @click="openPositionModal" class="position-link">
            {{ selectedPosition.title || 'לא זמין' }}
          </span>
          <span v-else>לא זמין</span>
        </h5>
        <div class="mt-3">
          <button @click="openPositionSwitchModal" class="button is-info">
            <span class="icon"><i class="fas fa-exchange-alt"></i></span>
            <span>החלף משרה</span>
          </button>
        </div>
        <div v-if="relatedPositionsCount < 5" class="notification is-warning">
          <p>⚠️ אזהרה: יש פחות מ-5 משרות אחיות ({{ relatedPositionsCount }})</p>
        </div>
        <div class="field mt-4">
          <label class="checkbox is-size-5">
            <input type="checkbox" v-model="confirmationChecked" />
            <span class="mr-2">אישור - הכל מכוון כמו שצריך ואפשר להמשיך</span>
          </label>
        </div>
        <button @click="proceedToScoring" class="button is-primary mt-3" :disabled="!confirmationChecked">
          המשך
        </button>
      </div>
    </div>

    <!-- Carousel Mode -->
    <div v-if="carouselMode && currentOffer" class="box my-4 carousel-container">
      <div class="content has-text-right">
        <div class="carousel-header">
          <h4 class="title is-4">{{ currentTypeLabel }}</h4>
          <div class="timer-display" :class="{'timer-red': isTimerRed, 'timer-blue': !isTimerRed}">
            <span class="timer-icon">⏱</span>
            <span class="timer-text">{{ formattedTimer }}</span>
          </div>
        </div>

        <PositionDetailsBlock
          v-if="currentOffer && currentOffer.position"
          :position="currentOffer.position"
          :showSwitchButton="true"
          @switch-position="switchPositionInCarousel"
          class="entity-section my-4"
        />

        <ApplicantDetailsBlock
          v-if="currentOffer && currentOffer.applicant"
          :applicant="currentOffer.applicant"
          :applicant-payload="getApplicantPayloadForGhanaFlag(currentOffer.applicant)"
          :ghana-flag-payload="getApplicantPayloadForGhanaFlag(currentOffer.applicant)"
          @open-follow-up="openFollowUp"
          @open-magic-wand="openMagicWandModal"
          @applicant-answered="handlePhoneCall"
        />

        <OfferDetailsBlock :offer="currentOffer" :showEditButton="true" />

        <div class="buttons is-centered mt-4">
          <button @click="finishCurrentOffer" class="button is-primary is-medium">
            מעבר להצעה הבאה
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoadingOffer" class="box my-4 has-text-centered">
      <p class="title is-5">טוען הצעה...</p>
      <progress class="progress is-primary" max="100"></progress>
    </div>

    <!-- Error/Empty State -->
    <div v-else-if="!showConfirmationUI && !carouselMode && selectedPositionId" class="box my-4 has-background-warning-light has-text-centered">
      <p class="title is-5">⚠️ אין מצב זמין</p>
      <p>אנא רענן את הדף</p>
    </div>
  </div>
</template>

<script>
import OfferDetailsBlock from '@/components/shared/organisms/OfferDetailsBlock.vue'
import ApplicantDetailsBlock from '@/components/shared/organisms/ApplicantDetailsBlock.vue'
import PositionDetailsBlock from '@/components/shared/organisms/PositionDetailsBlock.vue'
import entityPayloadMixin from '@/mixins/entityPayloadMixin'
import offerValidationMixin from '@/mixins/offerValidationMixin'

export default {
  name: 'DesignatedPosition',
  mixins: [entityPayloadMixin, offerValidationMixin],
  components: {
    OfferDetailsBlock,
    ApplicantDetailsBlock,
    PositionDetailsBlock
  },
  props: {
    alert: {
      type: Object,
      required: false,
      default: () => null
    },
    position: {
      type: Object,
      required: false,
      default: () => null
    },
    mondayTaskData: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data () {
    return {
      selectedPositionId: null,
      selectedPosition: null,
      relatedPositionsCount: 0,
      showConfirmationUI: false,
      confirmationChecked: false,
      carouselMode: false,
      currentType: 'related_positions',
      currentCounter: 1,
      localCurrentOffer: null,
      isLoadingOffer: false,
      typeLabels: {
        'related_positions': 'משרות אחיות',
        'strong': 'סטרונג',
        'purple': 'סגול',
        'method_10': 'מטוד 10'
      },
      typeOrder: ['related_positions', 'strong', 'purple', 'method_10'],
      consecutiveNoAlertsTypes: 0,
      editableMassageOffer: '',
      originalMassageOffer: '',
      isSaving: false,
      timerSeconds: 0,
      timerInterval: null
    }
  },
  computed: {
    currentOffer () {
      return this.alert && this.alert.resources && this.alert.resources.offer
        ? this.alert.resources.offer
        : this.localCurrentOffer
    },
    currentTypeLabel () {
      return this.typeLabels[this.currentType]
    },
    isModified () {
      return this.editableMassageOffer !== this.originalMassageOffer
    },
    formattedTimer () {
      const mins = Math.floor(this.timerSeconds / 60)
      const secs = this.timerSeconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
    isTimerRed () {
      return this.timerSeconds >= 40
    },
    parsedMessage () {
      if (!this.currentOffer || !this.currentOffer.message) {
        return { content: '', hasMetadata: false }
      }

      const message = this.currentOffer.message
      const dateMatch = message.match(/<message-date>(.*?)<\/message-date>/i)
      const ownerMatch = message.match(/<owner>(.*?)<\/owner>/i)

      if (dateMatch || ownerMatch) {
        let content = message
          .replace(/<messsage-meta>.*?<\/messsage-meta>/gi, '')
          .replace(/<message-meta>.*?<\/message-meta>/gi, '')
          .trim()

        return {
          content: content || message,
          hasMetadata: true,
          date: dateMatch ? dateMatch[1] : null,
          owner: ownerMatch ? ownerMatch[1] : null
        }
      }

      return { content: message, hasMetadata: false }
    }
  },
  watch: {
    currentOffer: {
      handler (newOffer) {
        if (newOffer) {
          this.editableMassageOffer = newOffer.massage_offer || ''
          this.originalMassageOffer = newOffer.massage_offer || ''
        }
      },
      immediate: true
    },
    alert: {
      handler (newAlert) {
        if (!this.carouselMode || !newAlert) return

        const hasNoAlerts = this.checkForNoAlerts(newAlert)

        if (hasNoAlerts) {
          this.handleNoAlerts()
        } else {
          this.consecutiveNoAlertsTypes = 0
        }
      },
      immediate: false,
      deep: true
    }
  },
  mounted () {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    this.restoreFromLocalStorage()
  },
  beforeUnmount () {
    this.stopTimer()
    this.saveToLocalStorage()
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    checkForNoAlerts (alert) {
      return alert.message === 'No alerts found' ||
             alert.resources === null ||
             (alert.resources && Object.keys(alert.resources).length === 0)
    },
    handleNoAlerts () {
      this.consecutiveNoAlertsTypes++

      if (this.consecutiveNoAlertsTypes >= this.typeOrder.length) {
        this.completeTask()
        return
      }

      this.moveToNextOfferType()
    },
    completeTask () {
      this.stopTimer()
      this.carouselMode = false
      this.localCurrentOffer = null
      this.clearLocalStorage()
      this.selectedPositionId = null
      this.selectedPosition = null
      this.showConfirmationUI = false
      this.showCompletionModal()
    },
    moveToNextOfferType () {
      const currentIndex = this.typeOrder.indexOf(this.currentType)
      const nextIndex = (currentIndex + 1) % this.typeOrder.length
      this.currentType = this.typeOrder[nextIndex]
      this.currentCounter = 1
      this.saveToLocalStorage()
      this.resetTimer()
      this.$emit('reload-same-task')
    },
    handleBeforeUnload () {
      this.saveToLocalStorage()
    },
    getStorageKey () {
      return 'designated_position_state'
    },
    saveToLocalStorage () {
      try {
        const state = {
          position_id: this.selectedPositionId,
          position: this.selectedPosition,
          offer_type: this.currentType,
          counter: this.currentCounter,
          carousel_mode: this.carouselMode,
          show_confirmation_ui: this.showConfirmationUI,
          confirmation_checked: this.confirmationChecked,
          related_positions_count: this.relatedPositionsCount,
          current_offer: this.localCurrentOffer,
          timer_seconds: this.timerSeconds,
          consecutive_no_alerts_types: this.consecutiveNoAlertsTypes,
          timestamp: Date.now()
        }
        localStorage.setItem(this.getStorageKey(), JSON.stringify(state))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving to localStorage:', error)
      }
    },
    restoreFromLocalStorage () {
      try {
        const savedState = localStorage.getItem(this.getStorageKey())
        if (!savedState) return

        const state = JSON.parse(savedState)

        const stateMapping = {
          position_id: 'selectedPositionId',
          position: 'selectedPosition',
          offer_type: 'currentType',
          counter: 'currentCounter',
          carousel_mode: 'carouselMode',
          show_confirmation_ui: 'showConfirmationUI',
          confirmation_checked: 'confirmationChecked',
          related_positions_count: 'relatedPositionsCount',
          current_offer: 'localCurrentOffer',
          consecutive_no_alerts_types: 'consecutiveNoAlertsTypes'
        }

        Object.keys(stateMapping).forEach(key => {
          if (state[key] !== undefined) {
            this[stateMapping[key]] = state[key]
          }
        })

        if (state.timer_seconds !== undefined && this.carouselMode) {
          this.timerSeconds = state.timer_seconds
          this.startTimer()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring from localStorage:', error)
        this.clearLocalStorage()
      }
    },
    async restorePosition (positionId) {
      try {
        const position = await this.$network_utils.getPosition({ id: positionId })

        if (position) {
          this.selectedPositionId = position.id
          this.selectedPosition = position

          const checkResponse = await this.$network_utils.checkPositionRelatedPositions({
            position_id: positionId
          })

          this.relatedPositionsCount = checkResponse.count

          if (this.relatedPositionsCount >= 5) {
            await this.autoProceedToCarousel()
          } else {
            this.showConfirmationUI = true
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error restoring position:', error)
      }
    },
    clearLocalStorage () {
      try {
        localStorage.removeItem(this.getStorageKey())
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error clearing localStorage:', error)
      }
    },
    startTimer () {
      this.stopTimer()
      this.timerSeconds = 0
      this.timerInterval = setInterval(() => {
        this.timerSeconds++
      }, 1000)
    },
    stopTimer () {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
    resetTimer () {
      this.timerSeconds = 0
      this.startTimer()
    },
    async handlePhoneCall () {
      if (!this.currentOffer || !this.currentOffer.applicant) return

      try {
        await this.$network_utils.postStat({
          title: 'Applicant answered phone',
          designator: 'applicant_answered_phone',
          status: 'active',
          owner: this.$utils.getOwner().name,
          priority: 5,
          resource_type: 'Applicant',
          resource_id: this.currentOffer.applicant.resource_id || this.currentOffer.applicant.id
        })

        this.$utils.emit('show_toast', { text: 'success', type: 'success' })

        const applicantData = {
          ...this.currentOffer.applicant,
          resource_id: this.currentOffer.applicant.resource_id || this.currentOffer.applicant.id
        }

        this.$store.commit('modals/create', {
          designator: 'resource-alerts-form-modal',
          component_name: 'ApplicantAlertsForm',
          modal_width: 2000,
          is_center_modal: true,
          data: applicantData,
          resource_type: 'Applicant',
          modal_title: `Alerts - ${this.$utils.getResourceFullName({ resource_type: 'Applicant', data: applicantData })} ${applicantData.phone_number || ''}`
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating phone call stat:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה ביצירת רשומת שיחה', type: 'error' })
      }
    },
    async autoProceedToCarousel () {
      try {
        // Fire and forget - no waiting for response
        this.$network_utils.postEvent({
          event: 'resource_score',
          current_type: 'Position',
          current_resource_id: this.selectedPositionId
        })

        this.showConfirmationUI = false
        this.confirmationChecked = false
        this.carouselMode = true
        this.currentType = 'related_positions'
        this.currentCounter = 1
        this.consecutiveNoAlertsTypes = 0
        this.startTimer()
        this.saveToLocalStorage()
        this.$emit('reload-same-task')
        this.$emit('scoring-completed')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in autoProceedToCarousel:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה במעבר להצעות', type: 'error' })
      }
    },
    async proceedToScoring () {
      if (!this.confirmationChecked) {
        this.$utils.emit('show_toast', { text: 'נא לאשר את הבדיקה' })
        return
      }

      if (!this.selectedPositionId) {
        this.$utils.emit('show_toast', { text: 'לא נבחרה משרה' })
        return
      }

      try {
        const checkResponse = await this.$network_utils.checkPositionRelatedPositions({
          position_id: this.selectedPositionId
        })

        if (checkResponse.count < 5) {
          this.$utils.emit('show_toast', {
            text: `עדיין יש רק ${checkResponse.count} משרות אחיות. נא להוסיף עוד.`,
            type: 'warning'
          })
          return
        }

        // Fire and forget - no waiting for response
        this.$network_utils.postEvent({
          event: 'resource_score',
          current_type: 'Position',
          current_resource_id: this.selectedPositionId
        })

        this.showConfirmationUI = false
        this.confirmationChecked = false
        this.carouselMode = true
        this.currentType = 'related_positions'
        this.currentCounter = 1
        this.consecutiveNoAlertsTypes = 0
        this.startTimer()
        this.saveToLocalStorage()
        this.$emit('reload-same-task')
        this.$emit('scoring-completed')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in proceedToScoring:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה במעבר להצעות', type: 'error' })
      }
    },
    showCompletionModal () {
      this.$store.commit('modals/create', {
        designator: 'designated-position-completion-modal',
        component_name: 'DesignatedPositionCompletionModal',
        modal_width: 600,
        is_center_modal: true,
        modal_title: '🎉 סיום עבודה על משרה יעודית',
        props: {
          html_content: `
            <div style="text-align: center; padding: 2rem;">
              <div style="font-size: 4rem; margin-bottom: 1rem;">✅</div>
              <h2 style="font-size: 1.5rem; font-weight: bold; color: #2c3e50; margin-bottom: 1rem;">
                כל ההצעות לעבודה עובדו בהצלחה!
              </h2>
              <p style="font-size: 1.1rem; color: #555; margin-bottom: 1.5rem;">
                סיימת לעבור על כל ההצעות במשרה זו
              </p>
              <p style="font-size: 1rem; color: #666;">
                ניתן לבחור משרה חדשה ולהמשיך
              </p>
            </div>
          `
        }
      })
    },
    cancelEdit () {
      this.editableMassageOffer = this.originalMassageOffer
    },
    openEditMassageModal () {
      if (!this.currentOffer) return

      const applicantName = this.currentOffer.applicant
        ? `${this.currentOffer.applicant.first_name || ''} ${this.currentOffer.applicant.last_name || ''}`.trim()
        : 'N/A'

      const positionTitle = this.currentOffer.position && this.currentOffer.position.title ? this.currentOffer.position.title : 'N/A'
      const companyName = this.currentOffer.position && this.currentOffer.position.company && this.currentOffer.position.company.name ? this.currentOffer.position.company.name : ''

      const modalTitle = `Message - ${applicantName} - ${positionTitle}${companyName ? ' - ' + companyName : ''}`

      const offerData = {
        ...this.currentOffer,
        full_name: applicantName
      }

      this.$store.commit('modals/create', {
        component_name: 'MessageForm',
        modal_title: modalTitle,
        designator: 'message-modal',
        resource_data: offerData,
        additional_data: {
          endpoint: '/offers/',
          headers: 'admin_headers',
          resource_type: 'Offer'
        },
        is_submitable: true,
        modal_id: `ki${Math.random().toString(36).substr(2, 9)}`
      })
    },
    async finishCurrentOffer () {
      // Validate offer first
      await this.validateOfferBeforeMove()
    },
    async proceedToNextOffer () {
      this.currentCounter++

      if (this.currentCounter > 5) {
        await this.moveToNextOfferType()
      } else {
        this.saveToLocalStorage()
        this.resetTimer()
        this.$emit('reload-same-task')
      }
    },
    openSendCvModal () {
      if (!this.currentOffer) return

      this.$store.commit('modals/create', {
        designator: 'send-cv-mode-selection-modal',
        component_name: 'SendCvModeSelectionModal',
        modal_width: 1500,
        data: this.currentOffer,
        modal_title: 'Send CV'
      })
    },
    clickedCvOther () {
      if (!this.currentOffer) return

      this.$store.commit('modals/create', {
        designator: 'position-search-for-cv-modal',
        component_name: 'PositionSearchForCvModal',
        modal_title: 'בחירת משרה לשליחת קו"ח',
        modal_width: 800,
        is_center_modal: true,
        offer: this.currentOffer,
        onPositionSelected: (selectedPosition) => {
          this.openSendCvModalForOtherPosition(this.currentOffer, selectedPosition)
        }
      })
    },
    async openSendCvModalForOtherPosition (currentOffer, selectedPosition) {
      try {
        const offer = await this.findOrCreateOffer(
          currentOffer.applicant.resource_id || currentOffer.applicant.id,
          selectedPosition.id
        )

        this.$store.commit('modals/create', {
          designator: 'send-cv-mode-selection-modal',
          component_name: 'SendCvModeSelectionModal',
          data: offer,
          modal_title: 'בחירת מצב שליחת קורות חיים',
          modal_width: 600,
          is_center_modal: true
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error opening CV modal:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת/יצירת ההצעה' })
      }
    },
    async findOrCreateOffer (applicantId, positionId) {
      try {
        const offers = await this.$network_utils.getResourceOffers({
          resource_type: 'Position',
          resource_id: positionId,
          applicant_id: applicantId,
          per: 1
        })

        if (offers && offers.length > 0) {
          return offers[0]
        }

        const position = await this.$network_utils.getPosition({ id: positionId })
        const applicant = await this.$network_utils.getApplicant({ id: applicantId })

        return {
          id: `temp_${Date.now()}`,
          applicant: applicant,
          position: position,
          status: { name: 'not_created_yet' },
          message: '',
          is_virtual: true
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error finding/creating offer:', error)
        throw error
      }
    },
    openOfferModal () {
      if (!this.currentOffer) return

      this.$store.commit('modals/create', {
        designator: 'offer-info-modal',
        component_name: 'ModalOffer',
        modal_width: 1500,
        data: this.currentOffer,
        modal_title: `הצעה #${this.currentOffer.id}`
      })
    },
    openPositionModal () {
      this.$store.commit('modals/create', {
        designator: 'position-info-modal',
        component_name: 'ModalPosition',
        modal_width: 1500,
        data: this.selectedPosition,
        modal_title: 'Position: ' + this.selectedPosition.title + (this.selectedPosition.company ? ', Company: ' + this.selectedPosition.company.name : '')
      })
    },
    openPositionModalFromOffer (event) {
      if (!this.currentOffer || !this.currentOffer.position) return

      const keepOpen = event && event.shiftKey
      const designator = keepOpen ? `position-info-modal-${Date.now()}` : 'position-info-modal'

      this.$store.commit('modals/create', {
        designator: designator,
        component_name: 'ModalPosition',
        modal_width: 1500,
        // עותק ולא ההצעה עצמה — הוספת הקשר המועמד לא אמורה לשנות את השורה בטבלה.
        // applicant_id מפעיל את פירוט הכישורים מהשיחה בתוך המודל.
        data: { ...this.currentOffer.position, ...this.$utils.applicantModalContext(this.currentOffer.applicant) },
        modal_title: 'Position: ' + this.currentOffer.position.title + (this.currentOffer.position.company ? ', Company: ' + this.currentOffer.position.company.name : '')
      })
    },
    openApplicantModal (event) {
      if (!this.currentOffer || !this.currentOffer.applicant) return

      const keepOpen = event && event.shiftKey
      const designator = keepOpen ? `applicant-info-modal-${Date.now()}` : 'applicant-info-modal'

      const applicantData = {
        ...this.currentOffer.applicant,
        resource_id: this.currentOffer.applicant.resource_id || this.currentOffer.applicant.id
      }

      this.$store.commit('modals/create', {
        designator: designator,
        component_name: 'ModalApplicant',
        modal_width: 1500,
        data: applicantData,
        modal_title: 'Applicant: ' + this.currentOffer.applicant.first_name + ' ' + this.currentOffer.applicant.last_name
      })
    },
    getOfferStatus () {
      if (!this.currentOffer || !this.currentOffer.status) return null

      if (typeof this.currentOffer.status === 'object') {
        return this.currentOffer.status.status || this.currentOffer.status.name || JSON.stringify(this.currentOffer.status)
      }

      return this.currentOffer.status
    },
    formatDate (dateString) {
      if (!dateString) return 'N/A'
      return this.$moment(dateString).format('DD/MM/YYYY HH:mm')
    },
    formatMessageDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        return this.$moment(dateString).format('DD/MM/YYYY HH:mm')
      } catch (error) {
        return dateString
      }
    },
    openPositionSwitchModal () {
      this.$store.commit('modals/create', {
        designator: 'position-search-for-solel-boneh-modal',
        component_name: 'PositionSearchForCvModal',
        modal_width: 800,
        payload: {
          offer: this.selectedPosition ? {
            position: this.selectedPosition
          } : null,
          onPositionSelected: this.handlePositionSelection.bind(this),
          fromSolelBoneh: true
        },
        modal_title: 'חיפוש משרה למשימה יעודית'
      })
    },
    async handlePositionSelection (newPosition) {
      if (!newPosition || !newPosition.id) {
        this.$utils.emit('show_toast', { text: 'משרה לא חוקית' })
        return
      }

      try {
        this.selectedPositionId = newPosition.id
        this.selectedPosition = newPosition

        const checkResponse = await this.$network_utils.checkPositionRelatedPositions({
          position_id: this.selectedPositionId
        })

        this.relatedPositionsCount = checkResponse.count

        this.$emit('position-changed', newPosition)

        this.$utils.emit('show_toast', {
          text: `משרה נבחרה בהצלחה: ${newPosition.title}`,
          type: 'success'
        })

        if (this.relatedPositionsCount >= 5) {
          await this.autoProceedToCarousel()
        } else {
          this.confirmationChecked = false
          this.showConfirmationUI = true
        }

        this.saveToLocalStorage()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error selecting position:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בבחירת המשרה' })
      }
    },
    openMagicWandModal () {
      if (!this.currentOffer || !this.currentOffer.applicant || !this.currentOffer.position) {
        this.$utils.emit('show_toast', { text: 'חסרים נתוני מועמד/משרה', type: 'warning' })
        return
      }

      const companyName = this.currentOffer.position.company && this.currentOffer.position.company.name
        ? this.currentOffer.position.company.name
        : ''

      this.$store.commit('modals/create', {
        designator: 'related-positions-modal',
        component_name: 'RelatedPositions',
        position: this.currentOffer.position,
        applicant: this.currentOffer.applicant,
        modal_title: `${this.currentOffer.position.title || 'משרה'} - ${companyName} - Related Positions`
      })
    },
    openFollowUp () {
      if (!this.currentOffer || !this.currentOffer.applicant) {
        this.$utils.emit('show_toast', { text: 'חסרים נתוני מועמד', type: 'warning' })
        return
      }

      const applicantData = {
        ...this.currentOffer.applicant,
        resource_id: this.currentOffer.applicant.resource_id || this.currentOffer.applicant.id
      }

      this.$store.commit('modals/create', {
        designator: 'resource-alerts-form-modal',
        component_name: 'ApplicantAlertsForm',
        modal_width: 2000,
        is_center_modal: true,
        data: applicantData,
        resource_type: 'Applicant',
        modal_title: `Alerts - ${this.$utils.getResourceFullName({ resource_type: 'Applicant', data: applicantData })} ${applicantData.phone_number || ''}`
      })
    },
    switchPositionInCarousel () {
      this.$store.commit('modals/create', {
        designator: 'position-search-carousel-modal',
        component_name: 'PositionSearchForCvModal',
        modal_width: 800,
        payload: {
          offer: {
            position: this.selectedPosition
          },
          onPositionSelected: this.handlePositionSwitchInCarousel.bind(this),
          fromSolelBoneh: true
        },
        modal_title: 'החלף משרה במשימה יעודית'
      })
    },
    async handlePositionSwitchInCarousel (newPosition) {
      if (!newPosition || !newPosition.id) {
        this.$utils.emit('show_toast', { text: 'משרה לא חוקית' })
        return
      }

      try {
        this.stopTimer()
        this.carouselMode = false
        this.currentOffer = null
        this.selectedPositionId = newPosition.id
        this.selectedPosition = newPosition

        const checkResponse = await this.$network_utils.checkPositionRelatedPositions({
          position_id: this.selectedPositionId
        })

        this.relatedPositionsCount = checkResponse.count
        this.showConfirmationUI = true
        this.confirmationChecked = false
        this.currentType = 'related_positions'
        this.currentCounter = 1
        this.consecutiveNoAlertsTypes = 0
        this.saveToLocalStorage()

        this.$utils.emit('show_toast', {
          text: `משרה הוחלפה: ${newPosition.title}. אנא אשר להמשך.`,
          type: 'success'
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error switching position in carousel:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בהחלפת המשרה' })
      }
    }
  }
}
</script>

<style scoped>
.solel-boneh-wrapper {
  min-height: 100px;
  display: block !important;
  visibility: visible !important;
}

.box {
  display: block !important;
  visibility: visible !important;
}

.carousel-container {
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.carousel-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.5rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0;
  border-bottom: none;
}

.carousel-header > * {
  flex-shrink: 0;
}

.carousel-header .title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 150px;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  min-width: 70px;
  justify-content: center;
}

.timer-blue {
  background: #e3f2fd;
  color: #1976d2;
  border: 2px solid #1976d2;
}

.timer-red {
  background: #ffebee;
  color: #c62828;
  border: 2px solid #c62828;
  animation: pulse-timer 1.5s infinite;
}

@keyframes pulse-timer {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.timer-icon {
  font-size: 0.9rem;
}

.timer-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

.entity-section {
  padding: 0.3rem 0;
  margin-bottom: 0.2rem;
  background: #f8f9fa;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.entity-section:hover {
  background-color: #f0f9ff;
  border-color: #667eea;
}

.position-link {
  color: #3273dc;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.position-link:hover {
  color: #1d4ed8;
}
</style>
